import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { DateSelectArg, EventClickArg, EventDropArg } from "@fullcalendar/core";
import { ChevronLeft, ChevronRight, LogOut, Menu } from "lucide-react";
import CreateEventModal from "../Modals/CreateEventModal";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { googleLogout } from "@react-oauth/google";
import FullCalendar from "@fullcalendar/react";
import { toast } from "sonner";
import moment from "moment";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown";

import { getAllEvents, updateEvent } from "../../Api/api";
import LoadingModal from "../Modals/LoadingModal";
import { User } from "../../Utils/Types";
import { cn, isExceedingOverlap } from "../../Utils/Utils";
import { Button } from "../ui/button";

export interface EventType {
	_id?: string;
	id?: string;
	title: string;
	start: string;
	end: string;
	// email?: string;
	notes: string;
	contractorName: string;
	files: Images[];
	phoneNumber: string;
	address: string;
	timeSlot: "2 Hours" | "4 Hours" | "6 Hours" | "8 Hours" | null;
	color: string;
	userId: string;
	userName: string;
	userNickname: string;
}

export interface Images {
	filename: string;
	size: Number;
	url: string;
}

const MyCalendar = ({
	authenticatedUser,
	setAuthenticatedUser,
	setUserIsAuthenticated,
}: {
	authenticatedUser: User | null;
	setAuthenticatedUser: Dispatch<SetStateAction<User | null>>;
	setUserIsAuthenticated: Dispatch<SetStateAction<boolean>>;
}) => {
	const lastSwipeTime = useRef(0);
	const calendarRef = useRef<FullCalendar>(null);
	const [events, setEvents] = useState<EventType[]>([]);
	const [showWeekends, setShowWeekends] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [showCreateModal, setShowCreateModal] = useState<null | {
		open: boolean;
		event: DateSelectArg;
		id?: string;
		edit?: boolean;
	}>(null);

	const handleDateSelect = (selectInfo: DateSelectArg) => {
		setShowCreateModal({
			open: true,
			event: selectInfo,
		});
	};

	const handleEventClick = (clickInfo: EventClickArg) => {
		setShowCreateModal({
			open: true,
			id: clickInfo.event.id,
			edit: true,
			event: {
				start: clickInfo.event.start!,
				end: clickInfo.event.end!,
			} as DateSelectArg,
		});
	};

	const fetchEvents = async () => {
		setLoading(true);
		const response = await getAllEvents();
		if (response?.status === 200)
			setEvents([
				...response?.data?.data.map((item: any) => ({
					...item,
					id: item?._id,
				})),
			]);
		setLoading(false);
	};

	// For Chicago/Dallas time zone
	// const fetchEvents = async () => {
	// 	setLoading(true);
	// 	const response = await getAllEvents();
	// 	if (response?.status === 200) {
	// 		const adjustedEvents = response?.data?.data.map((item: any) => {
	// 			const start = new Date(item?.start);
	// 			const end = item?.end ? new Date(item?.end) : undefined;

	// 			// Manually convert to America/Chicago time zone
	// 			const chicagoStart = new Date(
	// 				start.toLocaleString("en-US", { timeZone: "America/Chicago" })
	// 			);
	// 			const chicagoEnd = end
	// 				? new Date(
	// 						end.toLocaleString("en-US", { timeZone: "America/Chicago" })
	// 				  )
	// 				: undefined;

	// 			// Log the event start and end times before returning
	// 			if (item.title.toLowerCase().includes("ryan")) {
	// 				console.log("item:", item);
	// 				console.log("Original Start:", start);
	// 				console.log("Chicago Start:", chicagoStart);
	// 				console.log("Original End:", end);
	// 				console.log("Chicago End:", chicagoEnd);
	// 			}

	// 			return {
	// 				...item,
	// 				id: item?._id,
	// 				start: chicagoStart.toISOString(), // Convert back to ISO string
	// 				end: chicagoEnd ? chicagoEnd.toISOString() : undefined,
	// 			};
	// 		});

	// 		setEvents(adjustedEvents);
	// 	}
	// 	setLoading(false);
	// };

	useEffect(() => {
		fetchEvents();
	}, []);

	const eventDrop = async (data: EventDropArg) => {
		const eventExists = events.find((item) => item._id === data?.event?.id);
		if (!eventExists) return;
		if (!data.event.start || !data.event.end) {
			toast.error("Error", {
				description: "Invalid event times.",
			});
			data.revert();
			return;
		}
		const updatedEvent: EventType = {
			...eventExists,
			start: data.event.start.toISOString(),
			end: data.event.end.toISOString(),
		};
		if (
			isExceedingOverlap(
				data.event.start,
				data.event.end,
				events,
				data.event.id
			)
		) {
			toast.error("Error", {
				description:
					"This time slot already has 3 jobs scheduled. Please choose another.",
			});
			data.revert();
			return;
		}
		// const isOverlapping = events.some((event) => {
		// 	if (event._id === data?.event?.id) return false;
		// 	const existingStart = moment(event.start);
		// 	const existingEnd = moment(event.end);
		// 	const newStart = moment(data.event.start);
		// 	const newEnd = moment(data.event.end);
		// 	return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
		// });
		// if (isOverlapping) {
		// 	toast.error("Error", {
		// 		description: "This time slot is already filled! Please choose another.",
		// 	});
		// 	data.revert();
		// 	return;
		// }
		setLoading(true);
		const response = await updateEvent(data?.event?.id, updatedEvent);
		if (response?.status !== 200) {
			toast.error("Error", {
				description: "Unable to move the job.",
			});
			data.revert();
		} else {
			setEvents((prevEvents) =>
				prevEvents.map((event) =>
					event._id === data?.event?.id ? updatedEvent : event
				)
			);
			toast("Success", {
				description: "Job moved successfully.",
			});
		}
		setLoading(false);
	};

	const handleLogout = () => {
		googleLogout();
		setAuthenticatedUser(null);
		setUserIsAuthenticated(false);
		sessionStorage.clear();
	};

	const handleMove = (move: "back" | "next") => {
		if (move === "next") calendarRef?.current?.getApi().next();
		else if (move === "back") calendarRef?.current?.getApi().prev();
	};

	const handleViewChange = (view: string) => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			calendarApi.changeView(view);
		}
	};

	const handleTodayClick = () => {
		if (calendarRef.current) {
			calendarRef.current
				.getApi()
				.gotoDate(moment(new Date()).startOf("isoWeek").format("YYYY-MM-DD"));
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!calendarRef.current) return;
			const calendarApi = calendarRef.current.getApi();
			if (event.key === "ArrowLeft") calendarApi.prev();
			else if (event.key === "ArrowRight") calendarApi.next();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	useEffect(() => {
		const handleWheel = (event: WheelEvent) => {
			const now = Date.now();
			if (now - lastSwipeTime.current < 800) return;
			if (event.deltaX > 30) {
				lastSwipeTime.current = now;
				calendarRef?.current?.getApi()?.next();
			} else if (event.deltaX < -30) {
				lastSwipeTime.current = now;
				calendarRef?.current?.getApi()?.prev();
			}
		};
		window.addEventListener("wheel", handleWheel, { passive: true });
		return () => window.removeEventListener("wheel", handleWheel);
	}, [calendarRef]);

	return (
		<>
			<div className='absolute top-4 left-4 z-10 overflow-hidden max-w-screen'>
				<DropdownMenu>
					<DropdownMenuTrigger className='outline-none data-[state=open]:bg-gray-500 data-[state=open]:text-white hover:bg-gray-500 bg-gray-200 duration-100 rounded-full text-gray-500 hover:text-white'>
						<Button variant={"outline"} className='border-none w-fit h-fit p-3'>
							<Menu className='min-h-6 min-w-6' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent className='bg-gray-50 p-2 ml-1'>
						<DropdownMenuItem
							className='dropdownMenuItem'
							onClick={handleTodayClick}
						>
							Go to Today
						</DropdownMenuItem>
						<DropdownMenuItem
							className='dropdownMenuItem'
							onClick={() => handleViewChange("weekView")}
						>
							Week View
						</DropdownMenuItem>
						<DropdownMenuItem
							className='dropdownMenuItem'
							onClick={() => handleViewChange("twoWeekView")}
						>
							Two Weeks View
						</DropdownMenuItem>
						<DropdownMenuItem
							className='dropdownMenuItem'
							onClick={() => setShowWeekends(!showWeekends)}
						>
							{showWeekends ? "Hide Sunday" : "Show Sunday"}
						</DropdownMenuItem>
						<DropdownMenuItem
							className='dropdownMenuItem text-destructive'
							onClick={handleLogout}
						>
							<LogOut /> Logout
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<Button
				className='arrowButton -left-3 hover:left-0'
				onClick={() => handleMove("back")}
			>
				<ChevronLeft className='min-h-6 h-6 min-w-6 w-6' />
			</Button>
			<Button
				className='arrowButton -right-3 hover:right-0'
				onClick={() => handleMove("next")}
			>
				<ChevronRight className='min-h-6 h-6 min-w-6 w-6' />
			</Button>
			<div className='w-full overflow-x-auto'>
				<div className='min-w-[1200px] sm:min-w-screen'>
					<FullCalendar
						ref={calendarRef}
						longPressDelay={500}
						eventLongPressDelay={500}
						plugins={[timeGridPlugin, interactionPlugin]}
						selectable={true}
						editable={true}
						events={events}
						eventStartEditable={true} // disable dragging start time by default
						eventDurationEditable={false} // disable changing event duration by dragging
						select={handleDateSelect}
						eventClick={handleEventClick}
						eventDrop={eventDrop}
						height='100vh'
						slotMinTime={
							authenticatedUser && authenticatedUser?.isDeveloper
								? "00:00:00"
								: "08:00:00"
						}
						slotMaxTime={
							authenticatedUser && authenticatedUser?.isDeveloper
								? "23:59:00"
								: "19:00:00"
						}
						slotDuration='01:00:00'
						// timeZone='America/Chicago'
						selectOverlap={false}
						initialDate={moment(new Date())
							.startOf("isoWeek")
							.format("YYYY-MM-DD")}
						weekends={true}
						allDaySlot={false}
						selectMirror={true}
						slotEventOverlap={false}
						locale={"en"}
						dayHeaderContent={(args) => {
							const date = new Date(args.date);
							const today = new Date();
							const isToday =
								date.getDate() === today.getDate() &&
								date.getMonth() === today.getMonth() &&
								date.getFullYear() === today.getFullYear();

							return (
								<div className='flex flex-col items-center text-center text-gray-700 h-full font-sans'>
									<span
										className={cn(
											"text-xs font-semibold",
											isToday && "text-themeColor"
										)}
									>
										{date.toLocaleDateString("en-US", { weekday: "short" }) +
											", " +
											moment(date).format("MMM YYYY")}
									</span>
									<span
										className={cn(
											"min-w-12 min-h-12 flex items-center justify-center rounded-full text-2xl mt-0.5 font-medium",
											isToday && "bg-themeColor text-white"
										)}
									>
										{date.getDate()}
									</span>
								</div>
							);
						}}
						views={{
							twoWeekView: {
								type: "timeGrid",
								duration: { days: 14 },
								buttonText: "2 Weeks",
								firstDay: 1,
							},
							weekView: {
								type: "timeGrid",
								duration: { days: 7 },
								buttonText: "Week",
								firstDay: 1,
							},
						}}
						headerToolbar={
							false && {
								left: "",
								center: "prev,title,next",
								right: "today weekView,twoWeekView toggleWeekends",
							}
						}
						slotLabelFormat={{
							hour: "2-digit",
							minute: "2-digit",
							hour12: true,
						}}
						dayHeaderFormat={{
							weekday: "long",
							month: "2-digit",
							day: "2-digit",
							year: "2-digit",
						}}
						customButtons={{
							toggleWeekends: {
								text: showWeekends ? "Hide Weekend" : "Show Weekend",
								click: () => setShowWeekends(!showWeekends),
							},
						}}
						hiddenDays={!showWeekends ? [0] : []}
						initialView='weekView'
					/>
				</div>
			</div>
			<CreateEventModal
				open={showCreateModal}
				closePopup={() => setShowCreateModal(null)}
				events={events}
				setEvents={setEvents}
				authenticatedUser={authenticatedUser}
			/>
			{loading && <LoadingModal />}
		</>
	);
};

export default MyCalendar;
